import { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { AutoCritiqueRequest, CandidateMessageWithMetadata } from '@scale/shared/types/chat';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { useModelCritiqueStore } from 'react-scale/components/scalers/tasks/useModelCritiqueStore';
import crypto from 'crypto';
import { CandidateState } from '@scale/shared/types/chat-completion';

export type ModelCritiqueHook = ReturnType<typeof useModelCritique>;

export type ModelCritiqueOutput = {
  severity: ModelCritiqueSeverity;
  critique: string;
  loading: boolean;
  fetched: boolean;
};

export enum ModelCritiqueSeverity {
  None = 'none',
  Minor = 'minor',
  Moderate = 'moderate',
  Major = 'major',
  Critical = 'critical',
  Missing = 'missing',
}

export const mapModelCritiqueSeverityToVariant = (severity: ModelCritiqueSeverity) => {
  switch (severity) {
    case ModelCritiqueSeverity.None:
      return 'info';
    case ModelCritiqueSeverity.Minor:
      return 'info';
    case ModelCritiqueSeverity.Moderate:
      return 'warning';
    case ModelCritiqueSeverity.Major:
      return 'danger';
    case ModelCritiqueSeverity.Critical:
      return 'danger';
    case ModelCritiqueSeverity.Missing:
      return 'danger';
    default:
      return 'info';
  }
};

const ERROR_MESSAGE =
  'There was an error fetching the critique. Please wait a few seconds and try again.';

const SEVERITY_FORMAT = 'Severity:';

/**
 * Determines the severity level of an ModelCritique response. Returns None if the expected
 * severity format is guaranteed to not exist at all in the response. Returns Missing if
 * there is not enough information yet to determine the severity level due to streaming.
 * Otherwise, returns the parsed severity level.
 *
 * @param critique - The ModelCritique response to evaluate.
 * @returns The parsed severity level from the ModelCritique response.
 */
const getSeverityLevel = (critique: string): ModelCritiqueSeverity => {
  if (critique === ERROR_MESSAGE) {
    return ModelCritiqueSeverity.Missing;
  }
  const lines = critique.split('\n');
  if (lines.length === 0) {
    return ModelCritiqueSeverity.Missing;
  }
  const firstLine = lines[0];
  if (!firstLine.startsWith(SEVERITY_FORMAT)) {
    if (firstLine.length >= SEVERITY_FORMAT.length) {
      return ModelCritiqueSeverity.None;
    }
    return ModelCritiqueSeverity.Missing;
  }
  const severity = firstLine.split(':')[1].trim().toLowerCase();
  if (Object.values(ModelCritiqueSeverity).includes(severity as ModelCritiqueSeverity)) {
    return severity as ModelCritiqueSeverity;
  }
  return ModelCritiqueSeverity.Missing;
};

const useModelCritique = (response?: string) => {
  const [critiqueData, setCritiqueData] = useState<ModelCritiqueOutput>({
    severity: response ? getSeverityLevel(response) : ModelCritiqueSeverity.Missing,
    critique: response || '',
    loading: false,
    fetched: false,
  });

  const getBlockOnCritiqueSeverity = useCallback(
    (response: string, blockCriticalSeverity: boolean, regexToBlock: string) => {
      if (regexToBlock) {
        return new RegExp(regexToBlock).test(response);
      } else if (blockCriticalSeverity) {
        return [ModelCritiqueSeverity.Critical, ModelCritiqueSeverity.Major].includes(
          getSeverityLevel(response),
        );
      }
      return false;
    },
    [],
  );

  const getModelCritique: (params: AutoCritiqueRequest) => Promise<ModelCritiqueOutput> =
    useCallback(async (params: AutoCritiqueRequest) => {
      setCritiqueData({
        severity: ModelCritiqueSeverity.Missing,
        critique: '',
        loading: true,
        fetched: false,
      });
      let responseText = '';
      return new Promise(resolve => {
        try {
          apiFetch(null, 'internal/genai/autoCritique', {
            method: 'POST',
            body: params,
            fetchEventSourceOptions: {
              openWhenHidden: true,
              onopen: async response => {
                if (!response.ok) {
                  throw new Error('Failed to get model response. Please try again.');
                }
              },
              onmessage: event => {
                if (event.data === '[DONE]' || event.data === '[IGNORE]') {
                  return;
                }
                const parsed = JSON.parse(event.data);
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
                if (parsed.fullOutput) {
                  responseText = parsed.fullOutput;
                } else if (parsed.output) {
                  responseText += parsed.output;
                } else if (parsed.choices?.[0]?.delta?.content) {
                  responseText += parsed.choices[0].delta.content as string;
                }
                setCritiqueData({
                  severity: getSeverityLevel(responseText),
                  critique: responseText,
                  loading: true,
                  fetched: false,
                });
              },
              onclose: () => {
                setCritiqueData({
                  severity: getSeverityLevel(responseText),
                  critique: responseText,
                  loading: false,
                  fetched: true,
                });
                resolve({
                  severity: getSeverityLevel(responseText),
                  critique: responseText,
                  loading: false,
                  fetched: true,
                });
              },
              onerror: e => {
                throw e;
              },
            },
          });
        } catch (e) {
          setCritiqueData({
            severity: ModelCritiqueSeverity.Missing,
            critique: ERROR_MESSAGE,
            loading: false,
            fetched: true,
          });
          resolve({
            severity: ModelCritiqueSeverity.Missing,
            critique: ERROR_MESSAGE,
            loading: false,
            fetched: true,
          });
        }
      });
    }, []);

  const clearCritiqueData = useCallback(() => {
    setCritiqueData({
      severity: ModelCritiqueSeverity.Missing,
      critique: '',
      loading: false,
      fetched: false,
    });
  }, []);

  return {
    critiqueData,
    getBlockOnCritiqueSeverity,
    getModelCritique,
    clearCritiqueData,
  };
};

export const useModelCritiqueForCandidate = (
  enableModelCritique: boolean,
  candidate: CandidateMessageWithMetadata,
  displayIndex: number,
  chatPrompt: string,
) => {
  const taskContext = useContext(ChatTaskContext);
  const autoCritiqueHook = useModelCritique();
  const { getModelCritiqueState, setModelCritiqueState } = useModelCritiqueStore();

  const storeKey = useMemo(() => {
    return crypto
      .createHash('sha256')
      .update(`${displayIndex}-${chatPrompt}-${candidate.message.content}-modelResponseSelector`)
      .digest('hex');
  }, [displayIndex, chatPrompt, candidate.message.content]);

  const [autoCritiqueValue, setAutoCritiqueValue] = [
    getModelCritiqueState(storeKey),
    (autoCritique: ModelCritiqueOutput) => {
      setModelCritiqueState(storeKey, autoCritique);
    },
  ];

  const runAutoCritique = useCallback(() => {
    autoCritiqueHook
      .getModelCritique({
        projectId: taskContext.projectId,
        taskId: taskContext.taskId,
        assignmentId: taskContext.assignmentId,
        workerId: taskContext.workerId,
        scope: 'modelResponseSelector',
        variables: {
          prompt: chatPrompt,
          response: `${candidate.message.content}`,
        },
      })
      .then(autoCritiqueOutput => {
        setAutoCritiqueValue(autoCritiqueOutput);
        return autoCritiqueOutput;
      })
      .catch(() => {
        return;
      });
  }, [autoCritiqueHook, taskContext, chatPrompt, candidate.message.content, setAutoCritiqueValue]);

  useEffect(() => {
    if (
      !enableModelCritique ||
      autoCritiqueValue.critique ||
      candidate.state !== CandidateState.Completed
    ) {
      return;
    }
    runAutoCritique();
  }, [enableModelCritique, autoCritiqueValue.critique]);

  return {
    autoCritiqueValue,
    setAutoCritiqueValue,
    autoCritiqueHook,
  };
};

export default useModelCritique;
